// src/api/files.ts
import api from './baseApi';

export enum FileType {
  Avatar = 'avatar',
  Comment = 'comment',
  DunbarMedia = 'dunbar-media',
  ImageAvatar = 'image-avatar',
  ImageComment = 'image-comment',
  ImagePost = 'image-post',
  VideoComment = 'video-comment',
  VideoDirectMessage = 'video-direct-message',
  VideoPost = 'video-post',
  VideoWelcome = 'video-welcome',
  DocumentAttachment = 'document-attachment',
}

export interface FileDto {
  _id: string;
  name: string;
  contentType: string;
  url?: string;
  downloadURL?: string;
  ticket?: string;
}

export interface FileUploadInitResponse {
  uploadURL: string;
  key: string;
  file_id: string;
}

const isIso88591 = (value: string) => /^[\x00-\xFF]*$/.test(value);

export const isApiFileDownloadUrl = (fileUrl: string): boolean =>
  /\/files\/[^/?#]+\/download(?:[/?#]|$)/.test(fileUrl);

const hasDownloadTicketQuery = (fileUrl: string): boolean => {
  try {
    const params = new URL(fileUrl, 'https://example.invalid').searchParams;
    return (params.has('expires') && params.has('sig')) || params.has('ticket');
  } catch {
    return false;
  }
};

export const toAbsoluteApiUrl = (
  fileUrl: string,
  apiBaseUrl = api.defaults.baseURL || ''
): string => {
  if (/^https?:\/\//i.test(fileUrl)) {
    return fileUrl;
  }

  const base = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
  return new URL(fileUrl.replace(/^\//, ''), base).href;
};

export const getFileDownloadUrl = (
  fileId: string,
  file?: Pick<FileDto, 'url' | 'downloadURL' | 'ticket'>,
  apiBaseUrl = api.defaults.baseURL || ''
): string => {
  const candidates = [file?.downloadURL, file?.url].filter(
    (value): value is string => Boolean(value)
  );
  const apiDownload = candidates.find(isApiFileDownloadUrl);
  if (apiDownload) {
    return toAbsoluteApiUrl(apiDownload, apiBaseUrl);
  }

  const downloadUrl = new URL(
    `files/${fileId}/download`,
    apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`
  );
  if (file?.ticket) {
    downloadUrl.searchParams.set('ticket', file.ticket);
  }
  return downloadUrl.href;
};

export const toS3SafeFileName = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  const hasExtension = lastDot > 0 && lastDot < fileName.length - 1;
  const base = hasExtension ? fileName.slice(0, lastDot) : fileName;
  const extension = hasExtension ? fileName.slice(lastDot) : '';
  const safeBase = base
    .replace(/[\u2010-\u2015]/g, '-')
    .replace(/[\u2018\u2019\u201A]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    .replace(/[^\x00-\xFF]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  return `${safeBase || 'file'}${extension}`;
};

const hasUnsafeContentDisposition = (fileUrl: string): boolean => {
  try {
    const disposition = new URL(fileUrl).searchParams.get(
      'response-content-disposition'
    );
    return Boolean(disposition && !isIso88591(disposition));
  } catch {
    return false;
  }
};

export const getOpenableFileUrl = (
  file: Pick<FileDto, 'url' | 'downloadURL'>
): string | undefined => {
  const candidates = [file.url, file.downloadURL].filter(
    (value): value is string => Boolean(value)
  );
  return (
    candidates.find((candidate) => !hasUnsafeContentDisposition(candidate)) ??
    candidates[0]
  );
};

export const getNavigableFileDownloadUrl = (
  fileId: string,
  file?: Pick<FileDto, 'url' | 'downloadURL' | 'ticket'>,
  apiBaseUrl = api.defaults.baseURL || ''
): string | null => {
  const candidates = [file?.downloadURL, file?.url].filter(
    (value): value is string => Boolean(value)
  );
  const apiDownload = candidates.find(
    (candidate) =>
      isApiFileDownloadUrl(candidate) && hasDownloadTicketQuery(candidate)
  );
  if (apiDownload) {
    return toAbsoluteApiUrl(apiDownload, apiBaseUrl);
  }
  if (file?.ticket) {
    return getFileDownloadUrl(fileId, file, apiBaseUrl);
  }
  return null;
};

export const filesApi = {
  async initUpload(params: {
    name: string;
    contentType: string;
    type: FileType;
  }): Promise<FileUploadInitResponse> {
    const response = await api.post('/files/uploader', params);
    return response.data as FileUploadInitResponse;
  },

  async getFile(
    fileId: string,
    options?: { fresh?: boolean }
  ): Promise<FileDto> {
    const response = await api.get(`/files/${fileId}`, {
      params: options?.fresh ? { t: Date.now() } : undefined,
    });
    const data = response.data;
    const file = (data.result ?? data) as FileDto;
    return file;
  },
};
